import { Tag } from "../../components/interfaces/Tag";
import { extractDataFromXml, getDataFromCustomXml, persistDataToCustomXml } from "./CustomXmlHelper";
import { CoObjectTypeEnum } from "../../api/models";
import { CoObjectSummary } from "../../components/interfaces/CoObject";

export const CUSTOM_XML_TAG_NAMESPACE = "CustomXMLCoTag";

export interface EditTagResult {
  merged: boolean;
  sourceTagId?: string;
  destinationTagId?: string;
}

export const parseTagXmlToTags = (xml: string): Tag[] => {
  try {
    if (!xml) return [];
    const jsonText = extractDataFromXml(xml);
    if (!jsonText) {
      console.warn("[CustomXmlCoTagHelper][parseTagXmlToTags] CDATA section not found or empty.");
      return [];
    }
    const parsed = JSON.parse(jsonText);
    if (!Array.isArray(parsed)) {
      console.warn("[CustomXmlCoTagHelper][parseTagXmlToTags] JSON in CDATA is not an array.");
      return [];
    }
    return parsed
      .filter((t: any) => t && typeof t.id === "string" && typeof t.label === "string" && Array.isArray(t.objects))
      .map((t: any) => ({
        id: t.id as string,
        label: t.label as string,
        isDeleted: t.isDeleted === true,
        objects: (t.objects as any[])
          .filter((o: any) => o && typeof o.id === "string")
          .map((o: any) => {
            let readObject: CoObjectSummary = { id: o.id as string, objectName: "", objectType: o.objectType };
            if (o.objectType === CoObjectTypeEnum.TABLE_CELL) {
              readObject.row = o.row;
              readObject.column = o.column;
            }
            return readObject;
          }),
      }));
  } catch (e) {
    console.error("[CustomXmlCoTagHelper][parseTagXmlToTags] Error parsing XML/JSON:", e);
    return [];
  }
};

export const getTagsFromCustomXml = async (): Promise<Tag[]> => {
  const logPrefix = "[CustomXmlCoTagHelper][getTagsFromCustomXml]";
  return getDataFromCustomXml(CUSTOM_XML_TAG_NAMESPACE, parseTagXmlToTags, logPrefix);
};

export const editTagInCustomXml = async (tag: Tag): Promise<EditTagResult> => {
  const logPrefix = "[CustomXmlCoTagHelper][editTagInCustomXml]";
  try {
    console.debug(`${logPrefix} Editing tag:`, tag);
    const existingTags = await getTagsFromCustomXml();

    const sourceTag = existingTags.find((t) => t.id === tag.id);

    // Self-collision: renaming to the same label → normal update, no merge
    if (sourceTag && sourceTag.label === tag.label) {
      const updatedTags = existingTags.map((t) => (t.id === tag.id ? { ...tag, isDeleted: false } : t));
      await persistTagsToCustomXml(updatedTags);
      console.debug(`${logPrefix} Tag edited successfully`);
      return { merged: false };
    }

    // Find collision: prefer active over deleted
    const destination =
      existingTags.find((t) => t.id !== tag.id && t.label === tag.label && !t.isDeleted) ??
      existingTags.find((t) => t.id !== tag.id && t.label === tag.label && t.isDeleted === true);

    if (!destination) {
      // Case C: no collision — standard update
      const updatedTags = existingTags.map((t) => (t.id === tag.id ? { ...tag, isDeleted: false } : t));
      await persistTagsToCustomXml(updatedTags);
      console.debug(`${logPrefix} Tag edited successfully`);
      return { merged: false };
    }

    const originalLabel = sourceTag?.label ?? tag.label;

    if (!destination.isDeleted) {
      // Case A: merge into active tag — union objects, dedup by id
      const existingIds = new Set(destination.objects.map((o) => o.id));
      const newObjects = tag.objects.filter((o) => !existingIds.has(o.id));
      const mergedObjects = [...destination.objects, ...newObjects];
      const updatedTags = existingTags.map((t) => {
        if (t.id === destination.id) return { ...destination, objects: mergedObjects };
        if (t.id === tag.id) return { ...t, label: originalLabel, isDeleted: true };
        return t;
      });
      await persistTagsToCustomXml(updatedTags);
      console.debug(`${logPrefix} Tag merged into active tag ${destination.id}`);
      return { merged: true, sourceTagId: tag.id, destinationTagId: destination.id };
    } else {
      // Case B: restore deleted tag — replace objects with source's objects
      const updatedTags = existingTags.map((t) => {
        if (t.id === destination.id) return { ...destination, isDeleted: false, objects: tag.objects };
        if (t.id === tag.id) return { ...t, label: originalLabel, isDeleted: true };
        return t;
      });
      await persistTagsToCustomXml(updatedTags);
      console.debug(`${logPrefix} Tag merged into restored tag ${destination.id}`);
      return { merged: true, sourceTagId: tag.id, destinationTagId: destination.id };
    }
  } catch (e) {
    console.error(`${logPrefix} Error editing tag:`, e);
    throw e;
  }
};

export const addTagToCustomXml = async (tag: Tag): Promise<string> => {
  try {
    let tagId = tag.id;
    const existingTags = await getTagsFromCustomXml();
    let existingTagIndex = existingTags.findIndex((t) => t.label === tag.label && !t.isDeleted);
    if (existingTagIndex < 0) {
      existingTagIndex = existingTags.findIndex((t) => t.label === tag.label && t.isDeleted);
    }

    if (existingTagIndex >= 0) {
      const existingTag = existingTags[existingTagIndex];

      if (existingTag.isDeleted) {
        // reuse the deleted tag id, replace all objects
        existingTags[existingTagIndex] = { id: existingTag.id, label: tag.label, objects: tag.objects };
      } else {
        // merge objects
        const existingIds = new Set(existingTag.objects.map((o) => o.id));
        const newObjects = tag.objects.filter((o) => o.id && !existingIds.has(o.id));
        existingTags[existingTagIndex] = {
          id: existingTag.id,
          label: tag.label,
          objects: [...existingTag.objects, ...newObjects],
        };
      }
      tagId = existingTag.id;
    } else {
      // new tag
      existingTags.push(tag);
    }

    await persistTagsToCustomXml(existingTags);
    return tagId;
  } catch (e) {
    console.error("[CustomXmlCoTagHelper] addTagToCustomXml - Unexpected error:", e);
    return null;
  }
};

export const persistTagsToCustomXml = async (tags: Tag[]): Promise<void> => {
  const logPrefix = "[CustomXmlCoTagHelper][persistTagsToCustomXml]";
  const tagSerializer = (tags: Tag[]) =>
    tags.map((t) => ({
      id: t.id,
      label: t.label,
      ...(t.isDeleted ? { isDeleted: true } : {}),
      objects: (t.objects || [])
        .filter((o) => o && typeof o.id === "string")
        .map((o) => {
          let objectToAdd: CoObjectSummary = { id: o.id, objectType: o.objectType };
          if (o.objectType === CoObjectTypeEnum.TABLE_CELL) {
            let row = o.id.slice(o.id.indexOf("_") + 1, o.id.indexOf("-"));
            let col = o.id.slice(o.id.indexOf("-") + 1);
            objectToAdd.row = Number(row);
            objectToAdd.column = Number(col);
          }
          return objectToAdd;
        }),
    }));

  await persistDataToCustomXml(CUSTOM_XML_TAG_NAMESPACE, tags, tagSerializer, logPrefix);
};

export const deleteTagFromCustomXml = async (tagId: string): Promise<void> => {
  try {
    const existingTags = await getTagsFromCustomXml();
    const updatedTags = existingTags.map((t) => (t.id === tagId ? { ...t, isDeleted: true } : t));
    await persistTagsToCustomXml(updatedTags);
  } catch (e) {
    console.error("[CustomXmlCoTagHelper] deleteTagFromCustomXml - Unexpected error:", e);
  }
};
